// AccountMenu (DESIGN §3.1.6, UX §2.5): Kopf mit Person und Rollen-Chips → Konten (Kontowechsel) →
// „Add another account“ → Konto-Einträge → Theme → Plattformen → Hilfe → Abmelden.
// Desktop als Radix DropdownMenu (320 breit), mobil als Sheet von unten.
import { Check, SignOut, UserPlus } from '@phosphor-icons/react';
import { DropdownMenu } from 'radix-ui';
import { type ReactNode, useState } from 'react';
import { Avatar } from '../kern/Avatar';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';
import {
  type AccountMenuProps,
  AccountTrigger,
  MobileAccountSheet,
  RoleChips,
} from './AccountMenu.parts';
import { ThemeToggle } from './ThemeToggle';
import { useIsDesktop } from './parts';

export type {
  AccountMenuAccount,
  AccountMenuEntry,
  AccountMenuPerson,
  AccountMenuProps,
} from './AccountMenu.parts';

const itemClass =
  'flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-button text-fg outline-none select-none data-highlighted:bg-card-hover data-disabled:text-fg-disabled';

function Section({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <DropdownMenu.Group>
      {label ? (
        <DropdownMenu.Label className="px-3 pt-2 pb-1 text-chip text-fg-tertiary">
          {label}
        </DropdownMenu.Label>
      ) : null}
      {children}
    </DropdownMenu.Group>
  );
}

const Separator = () => <DropdownMenu.Separator className="mx-2 my-1 h-px bg-line-subtle" />;

export function AccountMenu(props: AccountMenuProps) {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const {
    person,
    accounts = [],
    entries = [],
    onSwitchAccount,
    onAddAccount,
    onSignOut,
    onSignOutAll,
  } = props;
  if (!isDesktop) return <MobileAccountSheet {...props} open={open} onOpenChange={setOpen} />;

  const byGroup = (group: string) => entries.filter((entry) => entry.group === group);
  const renderEntry = (entry: (typeof entries)[number]) => {
    const Glyph = entry.icon;
    const inner = (
      <>
        {entry.leading ??
          (Glyph ? <Glyph size={20} aria-hidden className="shrink-0 text-fg-secondary" /> : null)}
        <span className="min-w-0 flex-1 truncate">{entry.label}</span>
        {entry.trailing}
      </>
    );
    return (
      <DropdownMenu.Item
        key={entry.id}
        asChild={Boolean(entry.href)}
        className={itemClass}
        onSelect={entry.onSelect}
      >
        {entry.href ? (
          <RouterLink href={entry.href} reload={entry.reload}>
            {inner}
          </RouterLink>
        ) : (
          inner
        )}
      </DropdownMenu.Item>
    );
  };
  const platforms = byGroup('platforms');
  const help = byGroup('help');
  const multi = accounts.length > 1;

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <AccountTrigger {...props} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={props.align ?? 'end'}
          side={props.side ?? 'bottom'}
          sideOffset={8}
          collisionPadding={8}
          className="z-(--bar-z-popover) max-h-(--radix-dropdown-menu-content-available-height) w-80 overflow-y-auto rounded-xl bg-overlay p-2 shadow-2 data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in"
        >
          <div className="flex items-center gap-3 px-3 pt-2 pb-3">
            <Avatar
              name={person.name}
              id={person.id}
              src={person.avatarSrc}
              size={40}
              surface="overlay"
              decorative
            />
            <div className="min-w-0">
              <p className="truncate text-body font-semibold text-fg">{person.name}</p>
              <p className="truncate text-label text-fg-secondary">{person.email}</p>
              <RoleChips roles={person.roles} />
            </div>
          </div>
          {accounts.length > 0 ? (
            <>
              <Separator />
              <Section label="Accounts">
                <DropdownMenu.RadioGroup
                  value={accounts.find((a) => a.active)?.id}
                  onValueChange={(id) => onSwitchAccount?.(id)}
                >
                  {accounts.map((account) => (
                    <DropdownMenu.RadioItem
                      key={account.id}
                      value={account.id}
                      className={cn(itemClass, 'min-h-12')}
                    >
                      <Avatar
                        name={account.name}
                        id={account.avatarId ?? account.id}
                        src={account.avatarSrc}
                        size={28}
                        surface="overlay"
                        decorative
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{account.name}</span>
                        <span
                          className={cn(
                            'truncate text-label',
                            account.expired ? 'text-warning-fg' : 'text-fg-secondary',
                          )}
                        >
                          {account.expired ? 'Session expired · Sign in' : account.email}
                        </span>
                      </span>
                      <DropdownMenu.ItemIndicator>
                        <Check size={18} weight="bold" className="text-accent-fg" aria-hidden />
                      </DropdownMenu.ItemIndicator>
                    </DropdownMenu.RadioItem>
                  ))}
                </DropdownMenu.RadioGroup>
                {onAddAccount ? (
                  <DropdownMenu.Item className={itemClass} onSelect={onAddAccount}>
                    <UserPlus size={20} aria-hidden className="text-fg-secondary" />
                    Add another account
                  </DropdownMenu.Item>
                ) : null}
              </Section>
            </>
          ) : null}
          <Separator />
          <Section>{byGroup('account').map(renderEntry)}</Section>
          {props.showTheme === false ? null : (
            <div className="flex min-h-10 items-center justify-between gap-3 px-3 py-1">
              <span className="text-button text-fg">Theme</span>
              <ThemeToggle size="sm" iconsOnly />
            </div>
          )}
          {platforms.length > 0 ? (
            <>
              <Separator />
              <Section label="Platforms">{platforms.map(renderEntry)}</Section>
            </>
          ) : null}
          {help.length > 0 ? (
            <>
              <Separator />
              <Section>{help.map(renderEntry)}</Section>
            </>
          ) : null}
          <Separator />
          <DropdownMenu.Item className={itemClass} onSelect={onSignOut}>
            <SignOut size={20} aria-hidden className="text-fg-secondary" />
            Sign out
          </DropdownMenu.Item>
          {multi && onSignOutAll ? (
            <DropdownMenu.Item className={itemClass} onSelect={onSignOutAll}>
              <SignOut size={20} aria-hidden className="text-fg-secondary" />
              Sign out of all accounts
            </DropdownMenu.Item>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
